import styles from './UserExchangeField.module.scss';

import InfoIcon from '@mui/icons-material/Info';
import { Typography } from '@mui/material';
import { pick } from 'lodash';
import { useTranslation } from 'react-i18next';

import { ReactComponent as MInfoIcon } from '@work4all/assets/icons/m_info.svg';

import { useTenant } from '@work4all/data/lib/hooks/routing/TenantProvider';

import { FullUser } from '@work4all/models/lib/Classes/FullUser.entity';
import { UserSyncOptions } from '@work4all/models/lib/Classes/UserSyncOptions.entity';
import { EMode } from '@work4all/models/lib/Enums/EMode.enum';
import { SyncOptionsContactExport } from '@work4all/models/lib/Enums/SyncOptionsContactExport.enum';

import { Divider } from '../../../../dataDisplay/divider/Divider';
import { CheckboxRadioItem } from '../../../../input/checkbox-radio-item';
import { Collapse } from '../../../collapse';

import { ContactExportModeCheckboxes } from './ContactExportModeCheckboxes';

export const USER_SYNC_OPTIONS_FIELD: FullUser<EMode.query> = {
  syncOptions: [
    {
      id: null,
      userId: null,
      contactsExportAllowed: null,
      contactsExportMode: null,
      contactsLastSyncDate: null,
      updateTime: null,
      insertTime: null,
      appointmentsSync: null,
      appointmentsAllowPrivate: null,
      appointmentsSyncExternalAppointments: null,
      mailboxId: null,
      contactsIcludePrivateData: null,
      appointmentsSyncPrivateItems: null,
      tenantCode: null,
      contactsExportCustomerAllowed: null,
      contactsExportSupplierAllowed: null,
      contactsExportEmployeesAllowed: null,
      contactsExportCustomer: null,
      contactsExportEmployees: null,
      contactsExportSupplier: null,
    },
  ],
};

export interface UserExchangeFieldProps {
  value: FullUser;
  onChange: (opt: UserSyncOptions) => void;
  onUserChange: (user: FullUser) => void;
}

export const UserExchangeField = (props: UserExchangeFieldProps) => {
  const { value, onUserChange } = props;
  const { t } = useTranslation();

  const { activeTenant } = useTenant();

  const syncOption: UserSyncOptions = (value.syncOptions || []).find(
    (x) => x.tenantCode === activeTenant
  ) || {
    contactsExportMode: SyncOptionsContactExport.NO_EXPORT,
  };
  const current = pick(
    syncOption,
    'contactsExportCustomerAllowed',
    'contactsExportAllowed',
    'contactsExportSupplierAllowed',
    'contactsExportEmployeesAllowed',
    'contactsExportMode',
    'contactsIcludePrivateData',
    'appointmentsSync',
    'appointmentsSyncExternalAppointments',
    'appointmentsSyncPrivateItems',
    'contactsExportAllowed',
    'mailboxId',
    'contactsExportCustomer',
    'contactsExportEmployees',
    'contactsExportSupplier'
  );

  const onChange = (input: UserSyncOptions) => {
    props.onChange({
      ...current,
      ...input,
      id: syncOption.id,
    });
  };

  return (
    <div className={styles.container}>
      <div className={styles.warningContainer}>
        <MInfoIcon className={styles.mIcon} color="primary" />
        <Typography variant="body1" color={'black'}>
          {t('USER.EXCHANGE.WARNING')}
        </Typography>
      </div>

      <div className={styles.infoContainer}>
        <InfoIcon color="primary" />
        <Typography variant="body1" color={'black'}>
          {t('USER.EXCHANGE.INFO')}
        </Typography>
      </div>
      <Collapse defaultOpen title={t('USER.EXCHANGE.CONTACT_EXPORT')}>
        <CheckboxRadioItem
          label={t('USER.EXCHANGE.CUSTOMERS')}
          defaultChecked={current.contactsExportCustomerAllowed}
          onChange={(_, value) =>
            onChange({ contactsExportCustomerAllowed: value })
          }
        />
        <CheckboxRadioItem
          label={t('USER.EXCHANGE.SUPPLIERS')}
          defaultChecked={current.contactsExportSupplierAllowed}
          onChange={(_, value) =>
            onChange({ contactsExportSupplierAllowed: value })
          }
        />
        <CheckboxRadioItem
          label={t('USER.EXCHANGE.EMPLOYEES')}
          defaultChecked={current.contactsExportEmployeesAllowed}
          onChange={(_, value) =>
            onChange({ contactsExportEmployeesAllowed: value })
          }
        />
        <Divider />

        <ContactExportModeCheckboxes
          mode={
            current.contactsExportAllowed ?? SyncOptionsContactExport.NO_EXPORT
          }
          maximumAllowedMode={SyncOptionsContactExport.ALL_CONTACTS}
          onChange={(contactsExportAllowed) => {
            onChange({ contactsExportAllowed });
          }}
          withNone
        />

        <CheckboxRadioItem
          label={t('USER.EXCHANGE.INCLUDE_PRIVATE')}
          defaultChecked={current.contactsIcludePrivateData}
          onChange={(_, value) =>
            onChange({ contactsIcludePrivateData: value })
          }
        />
      </Collapse>

      <Collapse defaultOpen title={t('USER.EXCHANGE.APPOINTMENT_SYNC')}>
        <CheckboxRadioItem
          label={t('USER.EXCHANGE.ALLOW_SYNC')}
          checked={current.appointmentsSync}
          onChange={(_, value) => onChange({ appointmentsSync: value })}
        />
        <CheckboxRadioItem
          label={t('USER.EXCHANGE.IMPORT_MANUAL')}
          checked={
            current.appointmentsSync &&
            current.appointmentsSyncExternalAppointments
          }
          disabled={!current.appointmentsSync}
          onChange={(_, value) =>
            onChange({
              appointmentsSyncExternalAppointments: value,
            })
          }
        />
        <div className={styles.indent}>
          <CheckboxRadioItem
            label={t('USER.EXCHANGE.EXCLUDE_PRIVATE')}
            checked={
              current.appointmentsSync &&
              current.appointmentsSyncExternalAppointments &&
              !current.appointmentsSyncPrivateItems
            }
            disabled={
              !current.appointmentsSync ||
              !current.appointmentsSyncExternalAppointments
            }
            onChange={() =>
              onChange({
                appointmentsSyncPrivateItems:
                  !current.appointmentsSyncPrivateItems,
              })
            }
          />
        </div>
      </Collapse>
    </div>
  );
};
